import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { Workflow, WorkflowCreateRequest, WorkflowUpdateRequest } from "@/lib/types";
import { getErrorMessage } from "@/lib/utils";
import { toast } from "sonner";

export function useWorkflows() {
  const queryClient = useQueryClient();

  const workflowsQuery = useQuery({
    queryKey: ["workflows"],
    queryFn: async () => {
      return await apiClient.getWorkflows();
    },
  });

  const createWorkflowMutation = useMutation({
    mutationFn: async (data: WorkflowCreateRequest) => {
      return await apiClient.createWorkflow(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      queryClient.invalidateQueries({ queryKey: ["users-no-workflow"] });
      queryClient.invalidateQueries({ queryKey: ["departments-no-workflow"] });
      toast.success("Workflow created successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to create workflow"));
    },
  });

  const updateWorkflowMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: WorkflowUpdateRequest }) => {
      return await apiClient.updateWorkflow(id, data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      queryClient.invalidateQueries({ queryKey: ["workflow", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["users-no-workflow"] });
      queryClient.invalidateQueries({ queryKey: ["departments-no-workflow"] });
      toast.success("Workflow updated successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to update workflow"));
    },
  });

  const deleteWorkflowMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteWorkflow(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      queryClient.invalidateQueries({ queryKey: ["users-no-workflow"] });
      queryClient.invalidateQueries({ queryKey: ["departments-no-workflow"] });
      toast.success("Workflow deleted successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to delete workflow"));
    },
  });

  return {
    workflows: (workflowsQuery.data ?? []) as Workflow[],
    isLoading: workflowsQuery.isLoading,
    error: workflowsQuery.error,
    createWorkflow: createWorkflowMutation,
    updateWorkflow: updateWorkflowMutation,
    deleteWorkflow: deleteWorkflowMutation,
    refetch: () => queryClient.invalidateQueries({ queryKey: ["workflows"] }),
  };
}

export function useWorkflow(id: string | undefined) {
  return useQuery({
    queryKey: ["workflow", id],
    queryFn: async () => {
      if (!id) return null;
      return await apiClient.getWorkflowById(id);
    },
    enabled: !!id,
  });
}

export function useUserWorkflow(userId: string | undefined) {
  return useQuery({
    queryKey: ["user-workflow", userId],
    queryFn: async () => {
      if (!userId) return null;
      try {
        return await apiClient.getWorkflowByUser(userId);
      } catch (e: unknown) {
        const error = e as { message?: string; status?: number };
        if (error.message?.includes('not found') || error.status === 404) {
             return null;
        }
        throw e;
      }
    },
    enabled: !!userId,
  });
}

/** Workflows assigned to a department (for team leaders: department can create tasks if this returns at least one) */
export function useWorkflowsByDepartmentId(departmentId: string | undefined) {
  return useQuery({
    queryKey: ["workflows", "department", departmentId],
    queryFn: () => apiClient.getWorkflowsByDepartmentId(departmentId!),
    enabled: !!departmentId,
  });
}

export function useUsersWithoutWorkflow() {
  return useQuery({
    queryKey: ["users-no-workflow"],
    queryFn: async () => {
      return await apiClient.getUsersWithoutWorkflow();
    },
  });
}

export function useDepartmentsWithoutWorkflow() {
  return useQuery({
    queryKey: ["departments-no-workflow"],
    queryFn: async () => {
      return await apiClient.getDepartmentsWithoutWorkflow();
    },
  });
}
